import PropTypes from 'prop-types';
import React, { useRef, useEffect, useCallback, useState } from 'react';
import styled, { css } from 'styled-components';

import { RichTextRenderer } from 'common/components/RichTextEditor';

import { fixedFields } from '../../dynamicFields';

export const OVERFLOW_MODE_TRUNCATE = 'truncate';
export const OVERFLOW_MODE_WRAP = 'wrap';
export const OVERFLOW_MODE_SCALE = 'scale';

// Take the whole height of the parent and
// Override the global RTE styles conflict
const Container = styled.div`
  height: 100%;
  overflow: hidden;
  & .jodit-workplace {
    height: auto !important;
  }

  .jodit-wysiwyg,
  .jodit-wysiwyg p {
    ${props =>
      props.overflowMode === OVERFLOW_MODE_TRUNCATE &&
      css`
        word-wrap: break-word;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      `}
    ${props =>
      props.overflowMode === OVERFLOW_MODE_WRAP &&
      css`
        word-wrap: break-word;
        overflow: hidden;
      `}
  }

  .jodit-wysiwyg {
    ${props =>
      props.overflowMode === OVERFLOW_MODE_SCALE &&
      css`
        white-space: nowrap;
        transform-origin: top left;
        transform: scale(${props.scale ?? 1});

        // In RTE, when text is in center, it is achieve by jodit using <p> tag
        // however, <p> by default has "display: block;" css, which takes all of
        // the width available to it.
        //
        // However, when we apply the above "transform: scale(x)", the width of
        // <p> tag also shrink as a whole..
        // This is not a problem when text is left aligned.
        // The issue with <p> tag appears when center or right text-align is
        // applied using RTE and has multiple lines inside <p>.
        // Since we have "white-space: nowrap" to prevent text from falling to
        // next line, the long line inside <p> is unaffected.
        // But the small line in the same <p> is incorrectly aligned because
        // of the shrunk width.
        // Example:
        // <p style="text-align: center">
        //  this is long line which is unaffected
        //  <br>
        //  this is small line
        // </p>
        // <p style="text-align: right">
        //  this is long line which is unaffected
        //  <br>
        //  this is small line
        // </p>
        //
        // the following style recorrects the width to inverse of scaling
        // applied so that correctly aligning can be achieved
        p {
          width: ${((1 / props.scale) * 100).toFixed(2)}%;
        }
      `}
  }
`;

const HiddenContainer = styled(Container).attrs({ scale: 1 })`
  visibility: hidden;
`;

const getFieldDisplayValue = field => {
  if (!['single_select', 'multi_select'].includes(field.type)) {
    return field.value ?? '';
  }

  if (!field.optionsSelectedValues) {
    return '';
  }

  const optionsById = field.options.reduce((acc, option) => {
    acc[option.id] = option;
    return acc;
  }, {});

  return field.optionsSelectedValues
    .map(({ optionValueId, optionValueText }) => {
      if (optionValueText) {
        return optionValueText;
      }

      return optionsById[optionValueId].name;
    })
    .join(', ');
};

const Text = ({ readOnly, overflowMode, richText, person }) => {
  const container = useRef(null);
  const [scale, setScale] = useState(1);

  const dynamicFields = (
    !readOnly || !person
      ? []
      : [...fixedFields().map(f => ({ ...f, value: f.value({ person }) })), ...person.customFields]
  ).map(f => ({ ...f, value: getFieldDisplayValue(f) }));

  const recalculateScale = useCallback(() => {
    if (overflowMode !== OVERFLOW_MODE_SCALE) {
      setScale(1);
      return;
    }
    const newScale = !container.current
      ? 1
      : Math.min(
          container.current.clientWidth / container.current.scrollWidth,
          container.current.clientHeight / container.current.scrollHeight,
        );
    setScale(newScale);
  }, [overflowMode]);

  // Recalculate the scale when the fonts are loaded
  const onFontStatus = useCallback(
    ({ status }) => {
      if (status !== 'loaded') {
        return;
      }
      recalculateScale();
    },
    [recalculateScale],
  );

  // Recalculate the scale when data affecting the rendering changes
  useEffect(() => {
    recalculateScale();
  }, [overflowMode, richText, person, recalculateScale]);

  return (
    <>
      <Container overflowMode={overflowMode} scale={scale}>
        <RichTextRenderer content={richText} dynamicFields={dynamicFields} />
      </Container>
      {
        /*
          We have a "hidden" copy of the same with scale=1, so that we can
          find the unscaled scrollWidth/Height of the container
        */
        overflowMode === OVERFLOW_MODE_SCALE && (
          <HiddenContainer overflowMode={overflowMode} ref={container}>
            <RichTextRenderer
              content={richText}
              dynamicFields={dynamicFields}
              onFontStatus={onFontStatus}
            />
          </HiddenContainer>
        )
      }
    </>
  );
};

Text.propTypes = {
  readOnly: PropTypes.bool,
  overflowMode: PropTypes.oneOf([OVERFLOW_MODE_TRUNCATE, OVERFLOW_MODE_WRAP, OVERFLOW_MODE_SCALE]),
  richText: PropTypes.string,
  person: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    companyName: PropTypes.string,
    title: PropTypes.string,
    customFields: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string,
      }),
    ),
  }),
};

export default Text;
